'use client';

import { flushSync } from 'react-dom';
import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useCallback,
} from 'react';
import { imageIndexDB } from '../utils/imageIndexDB';

// 定义基础图像类型
export type BaseImageStatus = {
  // 基本信息
  localImgId: string; // uuidv4
  localImgName: string; // 被上传图像的名称
  localImgType: string; // 被上传图像的类型
  localImgSize: { width: number; height: number }; // 被上传图像的尺寸

  // 生成状态
  generateStatus: 'generating' | 'success' | 'failed';
  generateStatusText: string;
  localImgUrl: string; // 被上传图像的本地链接
  localFile: File; // 被上传图像的本地文件

  // 上传状态
  uploadStatus: 'ready' | 'uploading' | 'success' | 'failed';
  uploadStatusText: string;
  uploadUrl: string | null; // 上传后返回的url
  uploadId: string | null; // 上传后返回的fileId

  // 输出相关
  batchTaskId: string | null; // 批量任务ID
  outputImageUrl: string | null; // 输出图像的链接
  outputThumbnail: string | null; // 输出图像的缩略图
  outputError: string | null; // 输出图像的错误信息
  outputRate: number | null; // 输出图像的评分
};

// 状态类型定义，使用泛型
export interface ImageProcessingState<T extends BaseImageStatus> {
  images: Map<string, T>;
}

// 修改 Action 类型定义，使用泛型
export type ImageProcessingAction<T extends BaseImageStatus> =
  | {
      type: 'ADD_IMAGES_BATCH';
      payload: Array<{ id: string; status: T }>;
    }
  | {
      type: 'UPDATE_IMAGE';
      payload: { id: string; status: Partial<T> };
    }
  | {
      type: 'UPDATE_IMAGES_BATCH';
      payload: Array<{ id: string; status: Partial<T> }>;
    }
  | { type: 'REMOVE_IMAGE'; payload: { id: string } }
  | { type: 'CLEAR_IMAGES' };

// 创建一个通用的Reducer，使用泛型
export function createImageProcessingReducer<T extends BaseImageStatus>() {
  return function imageProcessingReducer(
    state: ImageProcessingState<T>,
    action: ImageProcessingAction<T>
  ): ImageProcessingState<T> {
    switch (action.type) {
      case 'ADD_IMAGES_BATCH': {
        const newImages = new Map(state.images);

        // 批量添加所有图像
        action.payload.forEach((item) => {
          newImages.set(item.id, item.status);
          // 异步保存到 IndexDB
          imageIndexDB.saveImage(item.id, item.status);
        });

        return { ...state, images: newImages };
      }

      case 'UPDATE_IMAGE': {
        const newImages = new Map(state.images);
        const currentStatus = newImages.get(action.payload.id);
        if (currentStatus) {
          const updatedStatus = {
            ...currentStatus,
            ...action.payload.status,
          } as T;
          newImages.set(action.payload.id, updatedStatus);

          // 异步更新 IndexDB
          imageIndexDB.saveImage(action.payload.id, updatedStatus);
        }
        return { ...state, images: newImages };
      }

      case 'UPDATE_IMAGES_BATCH': {
        const newImages = new Map(state.images);

        // 批量更新所有图像
        action.payload.forEach((item) => {
          const currentStatus = newImages.get(item.id);
          if (currentStatus) {
            const updatedStatus = {
              ...currentStatus,
              ...item.status,
            } as T;
            newImages.set(item.id, updatedStatus);

            // 异步更新 IndexDB
            imageIndexDB.saveImage(item.id, updatedStatus);
          }
        });

        return { ...state, images: newImages };
      }

      case 'REMOVE_IMAGE': {
        const newImages = new Map(state.images);
        newImages.delete(action.payload.id);

        // 异步从 IndexDB 删除
        imageIndexDB.removeImage(action.payload.id);

        return { ...state, images: newImages };
      }

      case 'CLEAR_IMAGES': {
        // 清空 IndexDB
        imageIndexDB.clearImages();

        return { ...state, images: new Map() };
      }

      default:
        return state;
    }
  };
}

// 创建一个通用的Context创建工厂
export function createImageProcessingContext<T extends BaseImageStatus>() {
  // Context
  type ImageProcessingContextType = {
    ImageState: ImageProcessingState<T>;
    dispatchImage: React.Dispatch<ImageProcessingAction<T>>;
    clearImages: () => Promise<void>;
  };

  const Context = createContext<ImageProcessingContextType | null>(null);

  // 初始状态
  const initialState: ImageProcessingState<T> = {
    images: new Map(),
  };

  // Provider 组件
  function Provider({ children }: { children: ReactNode }) {
    const [ImageState, dispatchImage] = useReducer(
      createImageProcessingReducer<T>(),
      initialState
    );

    // 创建异步清除函数
    const clearImages = useCallback(async () => {
      // 先等待 IndexDB 清除完成
      await imageIndexDB.clearImages();
      return new Promise<void>(async (resolve) => {
        // 立即更新状态
        flushSync(() => dispatchImage({ type: 'CLEAR_IMAGES' }));
        resolve();
      });
    }, []);

    return (
      <Context.Provider value={{ ImageState, dispatchImage, clearImages }}>
        {children}
      </Context.Provider>
    );
  }

  // Hook
  function useImageProcessing() {
    const context = useContext(Context);
    if (!context) {
      throw new Error(
        'useImageProcessing must be used within ImageProcessingProvider'
      );
    }
    return context;
  }

  return {
    Provider,
    useImageProcessing,
  };
}
